<template>
	<div ref="stickyHeader" class="header-container" :class="{ scrolled: isScrolled }" elevation="0">
		<div
			class="d-flex number-container"
			:class="{ 'has-save-status': !isViewOnly, 'has-save-status--error': !isViewOnly && saveState.status === 'error' }"
			:style="freeTrialEnded ? 'top: 52px !important;' : ''"
			elevation="0">
			<v-row class="align-center w-100" no-gutters>
				<v-col :class="display.mobile.value && authenticated ? 'flex-grow-0 flex-shrink-0' : 'overflow-hidden min-w-0'">
					<div class="d-flex align-center ml-4">
						<v-btn
							v-if="display.mobile.value && authenticated"
							class="flex-shrink-0"
							rounded="xl"
							variant="plain"
							prepend-icon="icon-arrow-left"
							@click.stop="goBack"
							>Back</v-btn
						>
						<div v-else class="d-flex flex-column number w-100 overflow-hidden min-w-0">
							<div :title="name" class="name text-truncate">{{ name }}</div>
							<div class="d-flex align-center">
								<template v-if="entityType === 'conversation'">
									<span v-test="'conversation-number-identity'" class="d-inline-flex align-center">
										<v-icon v-test="'conversation-number-hashtag'" class="hashtag project-date">icon-hashtag</v-icon>
										<span v-test="'conversation-number'" class="number">{{ number }}</span>
									</span>
									<v-icon class="round-spacer">icon-round-solid</v-icon>
								</template>
								<div class="d-flex" :class="{ 'mt-1': entityType === 'project' }">
									<v-tooltip location="top">
										<template #activator="{ props }">
											<span
												v-test="`${entityType}-created-date`"
												class="date"
												:class="[signingUp ? 'd-none' : 'd-block']"
												v-bind="props">
												<span>
													Created {{ timezones.formatDateWithDayOfWeekShort(createdAt) }}
													{{ timezones.formatTimeOnly(createdAt) }}
												</span>
											</span>
										</template>
										<span>
											{{ timezones.formatDateWithDayOfWeekShort(createdAt) }}
											{{ timezones.formatTimeOnly(createdAt) }}
										</span>
									</v-tooltip>
								</div>
							</div>
							<!-- LSP-20787 (D19): the session pill mirrors the mobile block (EntityDetailPage.vue's EntityStatus) for every
							     conversation; (D18) the invite's delivery sits beside it behind a hairline, with its repair buttons
							     and inline box — unless the scheduled-call card carries it (deliveryInCard). `d-none d-md-flex`: from
							     600 to 959 px this block renders next to the page's mobile block, which carries the pill and the chip there. -->
							<div v-if="showSessionRow" class="sms-delivery-row d-none d-md-flex flex-wrap align-center ga-2 mt-2">
								<!-- `?? ''`: status is a required prop; a status-less entity must not trip a dev warning. -->
								<EntityStatus
									:status="conversation.status ?? ''"
									:entity-type="entityType"
									:initiator="conversation.initiator ?? Initiator.RECORDING"
									:appointment-name="conversation.appointmentName ?? ''"
									:source-type="conversation.sourceType ?? ''" />
								<template v-if="delivery && showDeliveryHalf">
									<!-- A hairline only between two things: EntityStatus renders nothing for NotStarted (its own rule), and the
									     chip renders nothing (and warns, B8) for a state the display table does not know. -->
									<v-divider v-if="!isNotStarted && isKnownDeliveryState" vertical class="sms-delivery-row__hairline" />
									<SmsDeliveryChip :state="delivery.state" :since="delivery.since" size="detail" />
									<SmsDeliveryActions
										v-if="canRepairDelivery"
										:conversation-id="entityId"
										:state="delivery.state"
										:email-invitations-allowed="emailInvitationsAllowed"
										@select="deliveryMode = $event"
										@resent="closeDeliveryBoxAfterSend" />
									<!-- The row's last child and full width, so the box wraps onto its own line directly under the row.
									     :key remounts it on a switch between the two boxes, so typed text never carries over (the banner's rule). -->
									<SmsDeliveryRepairPanel
										v-if="deliveryMode && canRepairDelivery"
										:key="deliveryMode"
										class="w-100"
										:conversation-id="entityId"
										:mode="deliveryMode"
										:customer="conversation.Customers?.[0] ?? null"
										:participants="conversation.Participants"
										@resent="closeDeliveryBoxAfterSend"
										@customerUpdated="emit('deliveryCustomerUpdated', $event)"
										@refresh="emit('deliveryRefresh')"
										@close="deliveryMode = null" />
								</template>
							</div>
							<div v-if="entityType === 'conversation' && projectName" class="name text-truncate mw-90">
								Project:
								<a :title="projectName" class="text-decoration-underline cursor-pointer" @click="navigateToProject">
									{{ projectName }}
								</a>
							</div>
						</div>
					</div>
				</v-col>

				<v-col
					:cols="display.mobile.value ? undefined : 'auto'"
					:class="display.mobile.value ? ['flex-1', 'min-w-0'] : 'flex-shrink-0'">
					<v-card-actions
						v-if="authenticated"
						class="d-flex justify-end align-center flex-row flex-nowrap flex-1"
						:class="{ 'min-w-0 overflow-hidden': display.mobile.value }">
						<v-skeleton-loader v-if="permissionsLoading" type="button" aria-label="Loading project actions" />
						<EntityActions
							v-if="!permissionsLoading && entity && authenticated && !isViewOnly && !freeTrialEnded"
							:entity-type="entityType"
							:me="me"
							:entity="entity"
							:free-trial-ended="freeTrialEnded"
							:view-only="isViewOnly"
							:has-scribe-feature="hasScribeFeature"
							:is-external-editor="isExternalEditor"
							:can-share-project="canShareProject"
							:can-delete-project="canDeleteProject"
							:delivery="delivery"
							@refreshProject="emit('refreshProject')"
							@refreshProjectPermissions="refreshProjectPermissions"
							@toggleComments="emit('toggleComments')"
							@conversationCreated="conversationCreated"
							@uploadCounts="handleUploadCounts"
							@runReport="emit('runReport')"
							@invitationSent="emit('invitationSent', $event)" />
						<v-tooltip
							v-if="!permissionsLoading && entity && entityType === 'project' && isViewOnly && !freeTrialEnded"
							location="top"
							:text="shareDisabledMessage">
							<template #activator="{ props: tooltipProps }">
								<span v-bind="tooltipProps" tabindex="0" class="d-inline-flex flex-column align-center">
									<v-btn
										v-test="'project-share'"
										icon="icon-share-contact-stroke"
										variant="plain"
										aria-label="Share"
										:aria-description="shareDisabledMessage"
										disabled />
									<span class="text-caption">Share</span>
								</span>
							</template>
						</v-tooltip>
						<div v-if="!display.mobile.value" class="page-actions position-relative">
							<div class="page-actions-buttons d-flex justify-end align-center flex-row flex-nowrap ga-2">
								<v-btn
									:id="`${entityType}-back`"
									v-test="`${entityType}-back`"
									v-track="`${entityType}-back-button`"
									rounded="xl"
									elevation="0"
									variant="tonal"
									color="primary"
									class="page-action"
									:disabled="saving"
									@click.stop="goBack"
									>Back</v-btn
								>
								<v-btn
									:id="`${entityType}-save`"
									v-test="`${entityType}-save`"
									v-track="`${entityType}-save-button`"
									rounded="xl"
									elevation="0"
									color="primary"
									variant="flat"
									:loading="saving"
									class="page-action"
									:disabled="isViewOnly || isExternalEditor"
									@click.stop="emit('save')">
									Save
								</v-btn>
							</div>
							<!-- LSP-16420 follow-up: autosave status sits BELOW the Back/Save row, anchored absolutely so it
							     never reflows the buttons (the buttons must not shift up when the indicator appears). -->
							<SaveStatusIndicator
								v-if="!isViewOnly"
								class="page-actions-status position-absolute left-0 right-0"
								:status="saveState.status"
								:last-saved-at="saveState.lastSavedAt" />
						</div>
					</v-card-actions>
				</v-col>
			</v-row>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { Project } from "@/types/Project";
	import type { EntityType } from "@/types/EntityType";
	import { AccessType } from "@/enums/AccessType";
	import { computed, getCurrentInstance, onMounted, onUnmounted, ref, watch } from "vue";
	import EntityActions from "./EntityActions.vue";
	import EntityStatus from "./EntityStatus.vue";
	import SaveStatusIndicator from "./SaveStatusIndicator.vue";
	import { Conversation } from "@/types/Conversation";
	import timezones from "@/helpers/timezones";
	import { projectAccessFailureMessage, SHARE_PERMISSION_MESSAGE } from "@/helpers/userMessages";
	import { IDLE_SAVE_STATE, type SaveState } from "@/composables/useAutosave";
	import { useDisplay } from "vuetify";
	import SmsDeliveryChip from "@/components/SmsDeliveryChip.vue";
	import SmsDeliveryActions from "@/components/SmsDeliveryActions.vue";
	import SmsDeliveryRepairPanel from "@/components/SmsDeliveryRepairPanel.vue";
	import { Initiator } from "@/enums/Initiator";
	import constants from "@/api/constants";
	import type { ConversationDelivery } from "@/api/index.d";
	import { SMS_DELIVERY_DISPLAY, type SmsDeliveryRepairCustomer } from "@/helpers/smsDelivery";

	import type { FileUploadCounts } from "@/types/FileUploadCounts";

	const stickyHeader = ref<HTMLDivElement>();

	const instance = getCurrentInstance();
	const display = useDisplay();
	const moreOptionsOpened = ref(false);

	const props = withDefaults(
		defineProps<{
			entityId: string;
			entityType: EntityType;
			me: any;
			entity: Conversation | Project;
			name: string;
			number?: string | number;
			createdAt: string;
			isViewOnly: boolean;
			freeTrialEnded: boolean;
			authenticated: boolean;
			accessType: AccessType;
			saving: boolean;
			saveState?: SaveState;
			signingUp: boolean;
			projectName?: string;
			onBack?: () => void | Promise<void>;
			hasScribeFeature?: boolean;
			// A share-origin collaborator from a different org cannot copy the Project Link or edit About.
			isExternalEditor?: boolean;
			canShareProject?: boolean;
			canDeleteProject?: boolean;
			permissionsLoading?: boolean;
			permissionsUnavailable?: boolean;
			// LSP-20787: the session's SMS delivery state (null until loaded, and on every reload or switch).
			delivery?: ConversationDelivery | null;
			// True while the scheduled-call card carries the delivery (its banner is then the one delivery surface).
			deliveryInCard?: boolean;
			// D15 / S9: false hides every repair button; the sentence still shows.
			canRepairDelivery?: boolean;
		}>(),
		{
			entityId: undefined,
			isViewOnly: true,
			isExternalEditor: false,
			canShareProject: false,
			canDeleteProject: false,
			delivery: null,
			deliveryInCard: false,
			canRepairDelivery: false,
			saveState: () => ({ ...IDLE_SAVE_STATE })
		}
	);
	const shareDisabledMessage = computed(() => props.permissionsUnavailable ? projectAccessFailureMessage() : SHARE_PERMISSION_MESSAGE);

	const emit = defineEmits<{
		(e: "reset"): void;
		(e: "save"): void;
		(e: "toggleComments"): void;
		(e: "copyLink"): void;
		(e: "deleteProject"): void;
		(e: "refreshProject"): void;
		(e: "refreshProjectPermissions", item: any, accessType: any): void;
		(e: "conversationCreated", conversationId: string): void;
		(e: "uploadCounts", counts: FileUploadCounts): void;
		(e: "navigate-to-project"): void;
		(e: "runReport"): void;
		// Bugbot: conversationId is the id the send was actually made for (EntityDetailPage compares it to the
		// session it currently shows before refreshing anything for it).
		(e: "invitationSent", conversationId: string): void;
		(e: "deliveryResent", conversationId: string): void;
		(e: "deliveryRefresh"): void;
		(e: "deliveryCustomerUpdated", customer: SmsDeliveryRepairCustomer): void;
	}>();

	const conversation = computed(() => props.entity as Conversation);
	const isNotStarted = computed(() => conversation.value.status === constants.CONVERSATION_STATUS.NotStarted);
	const showDeliveryHalf = computed(() => !!props.delivery && !props.deliveryInCard);
	// EntityStatus renders nothing for NotStarted, so with no delivery half the row would be an empty gap (mt-2).
	const showSessionRow = computed(() => props.entityType === "conversation" && (!isNotStarted.value || showDeliveryHalf.value));
	// B8: the chip renders nothing (and warns) for a state the display table does not know — no hairline into nothing.
	const isKnownDeliveryState = computed(() => !!props.delivery && !!SMS_DELIVERY_DISPLAY[props.delivery.state]);
	// Final review I1: the booking forms' own reading of "Allow Email Invitations" (ActionSchedule.vue, ActionVideo.vue).
	const emailInvitationsAllowed = computed(() => props.me?.organization?.allowEmailInvitations !== false);

	// LSP-20787: which inline repair box the header row has open. A new delivery state — or none, which the page
	// passes on every reload and session switch — closes it, so a box never survives into another session.
	const deliveryMode = ref<"phone" | "email" | null>(null);
	watch(
		() => props.delivery?.state,
		() => {
			deliveryMode.value = null;
		}
	);

	// After any send from the row (Resend or the box): close the box; the page refetches the state once at +5 s.
	function closeDeliveryBoxAfterSend(conversationId: string) {
		deliveryMode.value = null;
		emit("deliveryResent", conversationId);
	}

	onMounted(() => {
		window.addEventListener("scroll", handleScroll);
	});

	onUnmounted(() => {
		window.removeEventListener("scroll", handleScroll);
	});

	// LSP-16427: `scrolled` is reactive and folded into the element's :class rather than being pushed
	// on with classList. This element's class attribute is itself a reactive :class binding, so Vue
	// rewrote the whole attribute on any re-render that touched it - silently wiping a manually-added
	// `scrolled` until the next scroll event fired. Since `.scrolled` is what applies `position: sticky`
	// and the top offset, the header visibly dropped and snapped back mid-scroll.
	//
	// The threshold also gets a hysteresis band: a single 65px trip point makes the header flip on every
	// sub-pixel scroll jitter around that value (and the sticky/non-sticky swap itself nudges layout,
	// which can re-trigger it). Engage at 65, release at 55.
	const SCROLL_ENGAGE_PX = 65;
	const SCROLL_RELEASE_PX = 55;
	const isScrolled = ref(false);

	function handleScroll() {
		const offset = window.scrollY;
		if (!isScrolled.value && offset > SCROLL_ENGAGE_PX) {
			isScrolled.value = true;
		} else if (isScrolled.value && offset < SCROLL_RELEASE_PX) {
			isScrolled.value = false;
		}
	}

	async function goBack() {
		if (props.onBack) {
			await props.onBack();
			return;
		}
		if (!route.query.from) {
			router.push("/");
		} else {
			router.go(-1);
		}
	}

	function refreshProjectPermissions(item: any, accessType: any) {
		emit("refreshProjectPermissions", item, accessType);
	}

	function conversationCreated(conversationId: string) {
		console.log("EntityHeader conversationCreated " + conversationId);
		emit("conversationCreated", conversationId);
	}

	function handleUploadCounts(counts: FileUploadCounts) {
		emit("uploadCounts", counts);
	}

	function navigateToProject(): void {
		emit("navigate-to-project");
	}
</script>

<style scoped lang="scss">
	.header-container {
		background-color: #f3f5f9;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1) !important;

		// LSP-16427: sticky at ALL times, with the offset it will actually stick at.
		//
		// This used to be `top: 0` with no `position`, i.e. statically positioned and the `top`
		// inert, and `.scrolled` switched it to `position: sticky; top: var(--shell-header-height)`
		// once past the threshold. Switching an element's `position` mid-scroll IS the glitch: at the
		// moment the class lands, the header leaves normal flow and re-anchors to the offset, so it
		// visibly snaps down (~13px here, i.e. the gap between where it sat in flow and the shell
		// chrome height). Reverting the class snapped it back — hence "glitching" rather than a
		// one-off jump.
		//
		// Declaring sticky up front costs nothing visually at rest: a sticky element stays in normal
		// flow until it would scroll above its `top`, then sticks. So the resting layout is
		// unchanged, and crossing the threshold no longer moves anything — `.scrolled` is now purely
		// cosmetic (see below).
		//
		// `top: 0`, NOT `var(--shell-header-height)`. The offset must not repeat the chrome height,
		// because this header's scroll context is already inset by it: App.vue gives `.main`
		// `padding-top: var(--shell-header-height)` to clear the fixed app bar. Measured on the real
		// page (65px app bar): with `top: var(--shell-header-height)` the header rendered at y=130
		// instead of y=65 — a full chrome height of dead space above it — while `top: 0` puts it at
		// y=65 at rest and holds it at y=65 when scrolled, i.e. flush under the app bar. This is why
		// it does NOT match styles/list-page.scss: those rules offset from a scroll context that is
		// not already padded, so the same variable is correct there and double-counts here.
		position: sticky;
		top: 0;
		z-index: 100;
		padding-top: 0px;
		padding-bottom: 0px;
		margin-bottom: 0px;
		border: 0;

		@media #{map.get($display-breakpoints, 'sm-and-down')} {
			background-color: #dff0ff;
		}

		// LSP-16427: cosmetics only. `position` / `top` / `z-index` moved to the base rule above so
		// that adding or removing this class can never change layout — only the square-off of the
		// top-left corner, which is what the scrolled state is meant to express.
		&.scrolled {
			border-top-left-radius: 0 !important;
		}
	}

	.number-container {
		color: #03011d99;
		padding: 4px 0;

		// Reserve room below the Back/Save row for the absolutely-positioned autosave status, but ONLY when
		// that status can actually render (.has-save-status === !isViewOnly). View-only entities hide the
		// indicator (`v-if="!isViewOnly"`), so reserving for them would leave permanent empty dead space
		// under the buttons — gate the reserve on the same condition so read-only headers stay tight.
		//
		// Desktop only (sm-and-up === !display.mobile.value, since mobileBreakpoint is "sm" → mobile is
		// width < 600px): the .page-actions block (and this status) only render on desktop, so mobile keeps
		// the tight 4px 0 padding. The gate MUST match the render gate (sm-and-up, not md-and-up) — otherwise
		// tablet widths 600–959px render the row+status with no reserve and it spills (LSP-16420). Buttons are
		// unaffected — the reserve sits below the row, so they don't shift.
		//
		// LSP-17163: size the reserve to the status's ACTUAL height per state instead of always reserving the
		// worst case. The steady states ("Saving…" / "Last saved …") are a single line, so a permanent 40px
		// band left a large visible dead-space stripe under the actions on every editor project/session page.
		// Reserve one line normally; only the error copy ("Couldn't save — we'll retry …") wraps to two lines
		// (~36px) in the ~208px button-block width, so widen to 40px solely in that state (see
		// .has-save-status--error below). The steady-state reserve never changes, so buttons still don't shift
		// in the common flow; only the rare error transition grows the band, which is acceptable versus a
		// permanent stripe.
		&.has-save-status {
			@media #{map.get($display-breakpoints, 'sm-and-up')} {
				padding-bottom: 22px;
			}
		}

		&.has-save-status--error {
			@media #{map.get($display-breakpoints, 'sm-and-up')} {
				padding-bottom: 40px;
			}
		}

		.date {
			color: #03011d66;
			font-size: 0.75rem;
			font-weight: 500;
			text-transform: uppercase;
			line-height: 1;
		}

		.name {
			color: #000;
			font-weight: 500;
		}

		.round-spacer {
			margin-left: 4px;
			margin-right: 4px;
			font-size: 8px;
			justify-content: center;
			align-content: center;
			align-items: center;
		}

		.number {
			font-size: 0.875rem;
			font-weight: 700;
		}
	}

	// LSP-20787: the prototype's 1px × 22px hairline between the session pill and the delivery chip.
	.sms-delivery-row__hairline {
		align-self: center;
		height: 22px;
	}

	.number-container .hashtag {
		width: 20px;
		height: 20px;
		font-size: 1.25rem;
	}

	.number-container :deep(.v-btn__prepend .icon-arrow-left) {
		transform: translateY(2px);
	}

	.hashtag::before {
		margin-left: 0;
	}

	.number-container > .number-container .number-container .page-actions {
		justify-content: flex-end;
	}

	.page-actions .v-btn.page-action {
		min-width: 100px;
		width: 100px;
		margin-bottom: unset;
	}

	// Below the Back/Save row, centered under the two buttons (LSP-16420 follow-up). left-0/right-0
	// span the button row (.page-actions, position-relative, shrink-wraps it), and display:block lets
	// a long status (e.g. the error message) reflow the icon+text inline and wrap to a second line
	// within that width with text-align:center instead of overflowing sideways. Out of flow, so it never
	// reflows the buttons. pointer-events:none — display-only; must never intercept clicks below the header.
	//
	// The `!important` on display is required, not incidental: SaveStatusIndicator's root already carries
	// `d-flex`, and Vuetify emits `.d-flex` AFTER `.d-block` (utility order: …block…flex…), so a `d-block`
	// utility class would lose the equal-specificity !important tie to the component's own `d-flex` and the
	// element would stay flex → long text overflows sideways. The scoped selector's `[data-v]` attribute
	// gives it higher specificity than the utilities, so this reliably wins.
	.page-actions-status {
		top: 100%;
		margin-top: 4px;
		pointer-events: none;
		display: block !important;
		text-align: center;
		line-height: 1.2;
	}

	.page-actions-status :deep(.save-status-text) {
		white-space: normal;
	}
</style>
